import React, { useState, useEffect } from 'react';
import { Download, Calendar, FileText, CheckCircle, Clock, User, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const ExportHistory = () => {
  const [filter, setFilter] = useState('all');
  const [exportHistory, setExportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadExportHistory();
  }, [currentPage, pageSize, filter]);

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const loadExportHistory = async () => {
    try {
      setLoading(true);
      
      // Calculate range for pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build query with filter
      let query = supabase
        .from('export_history')
        .select('*, user_profiles(full_name)', { count: 'exact' })
        .order('exported_at', { ascending: false });

      // Apply status filter if not 'all'
      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      // Apply pagination
      const { data, error, count } = await query.range(from, to);

      if (error) {
        // Table might not exist, use empty array
        console.warn('Export history table not found or error:', error);
        setExportHistory([]);
        setTotalCount(0);
        setTotalPages(1);
      } else {
        // Transform data to match component format
        // Note: We'll generate signed URLs on-demand when user clicks download
        // to avoid generating many signed URLs upfront
        const transformed = (data || []).map(item => {
          // Store file path for on-demand URL generation
          let filePath = null;
          if (item.file_path) {
            // Extract path within bucket (remove 'exports/' prefix if present)
            filePath = item.file_path.startsWith('exports/') 
              ? item.file_path.replace('exports/', '') 
              : item.file_path;
          }

          return {
            id: item.id,
            name: item.file_name || `${item.export_type}_${new Date(item.exported_at).toISOString().split('T')[0]}.${item.format.toLowerCase()}`,
            type: item.export_type.replace('_', ' '),
            format: item.format,
            generatedBy: item.user_profiles?.full_name || 'Admin',
            generatedAt: new Date(item.exported_at).toLocaleString('en-GB'),
            size: item.file_size ? formatFileSize(item.file_size) : 'N/A',
            records: item.record_count || 0,
            status: item.status || 'completed',
            filePath: filePath // Store for on-demand URL generation
          };
        });
        setExportHistory(transformed);
        
        // Update pagination info
        const total = count || 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / pageSize));
      }
    } catch (error) {
      console.error('Error loading export history:', error);
      setExportHistory([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Generate signed URL on-demand when user clicks download
  const handleDownload = async (item) => {
    if (!item.filePath) {
      alert('File path not available');
      return;
    }

    try {
      // Generate signed URL (works for both public and private buckets)
      const { data: signedUrlData, error: signedError } = await supabase.storage
        .from('exports')
        .createSignedUrl(item.filePath, 3600); // 1 hour expiration
      
      if (signedError) {
        console.error('Error generating signed URL:', signedError);
        // Fallback to public URL if bucket is public
        const { data: urlData } = supabase.storage
          .from('exports')
          .getPublicUrl(item.filePath);
        
        if (urlData?.publicUrl) {
          window.open(urlData.publicUrl, '_blank');
        } else {
          alert('Unable to generate download URL. Please try again.');
        }
      } else if (signedUrlData?.signedUrl) {
        window.open(signedUrlData.signedUrl, '_blank');
      } else {
        alert('Unable to generate download URL. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  // Calculate display range
  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  if (loading) {
    return (
      <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading export history...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter by:</span>
          <div className="flex gap-2">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Exports
            </button>
            <button
              onClick={() => handleFilterChange('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => handleFilterChange('processing')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'processing' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Processing
            </button>
            <button
              onClick={() => handleFilterChange('failed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'failed' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Failed
            </button>
          </div>
        </div>
      </div>
      {/* Export List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Generated By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {exportHistory?.map(item => (
                <tr key={item?.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item?.name}</p>
                        <p className="text-xs text-gray-500">{item?.records} records</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm text-gray-700">{item?.type}</span>
                    <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-600 rounded text-xs">
                      {item?.format}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 truncate">{item?.generatedBy}</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{item?.generatedAt}</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-700">
                    {item?.size}
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {item?.status === 'completed' ? (
                      <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-green-700">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Completed</span>
                        <span className="sm:hidden">Done</span>
                      </span>
                    ) : item?.status === 'failed' ? (
                      <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-red-700">
                        <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Failed</span>
                        <span className="sm:hidden">Fail</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-yellow-700">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Processing</span>
                        <span className="sm:hidden">Proc</span>
                      </span>
                    )}
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {item?.status === 'completed' ? (
                      item?.filePath ? (
                        <button
                          onClick={() => handleDownload(item)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">File not available</span>
                      )
                    ) : item?.status === 'failed' ? (
                      <span className="text-sm text-red-400">Export failed</span>
                    ) : (
                      <span className="text-sm text-gray-400">In Progress...</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-gray-200">
          {exportHistory?.map(item => (
            <div key={item?.id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item?.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">{item?.records} records</p>
                </div>
                {item?.status === 'completed' ? (
                  <span className="flex items-center gap-1 text-xs text-green-700 flex-shrink-0">
                    <CheckCircle className="w-3 h-3" />
                    <span className="hidden sm:inline">Completed</span>
                  </span>
                ) : item?.status === 'failed' ? (
                  <span className="flex items-center gap-1 text-xs text-red-700 flex-shrink-0">
                    <XCircle className="w-3 h-3" />
                    <span className="hidden sm:inline">Failed</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-yellow-700 flex-shrink-0">
                    <Clock className="w-3 h-3" />
                    <span className="hidden sm:inline">Processing</span>
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-2 sm:mb-3">
                <div>
                  <span className="text-gray-500">Type:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-700">{item?.type}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {item?.format}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Size:</span>
                  <p className="text-gray-700 mt-1">{item?.size}</p>
                </div>
                <div>
                  <span className="text-gray-500">Generated By:</span>
                  <div className="flex items-center gap-1 mt-1">
                    <User className="w-3 h-3 text-gray-400" />
                    <p className="text-gray-700 truncate">{item?.generatedBy}</p>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <p className="text-gray-700 text-xs">{item?.generatedAt}</p>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200">
                {item?.status === 'completed' ? (
                  item?.filePath ? (
                    <button
                      onClick={() => handleDownload(item)}
                      className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium touch-target"
                    >
                      <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                      Download
                    </button>
                  ) : (
                    <span className="text-xs sm:text-sm text-gray-400 block text-center py-1 sm:py-2">File not available</span>
                  )
                ) : item?.status === 'failed' ? (
                  <span className="text-xs sm:text-sm text-red-400 block text-center py-1 sm:py-2">Export failed</span>
                ) : (
                  <span className="text-xs sm:text-sm text-gray-400 block text-center py-1 sm:py-2">In Progress...</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>

            {/* Pagination Info and Controls */}
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-700">
                Showing {startIndex} to {endIndex} of {totalCount} entries
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-sm text-gray-700 px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && exportHistory?.length === 0 && (
        <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No exports found</h3>
          <p className="text-gray-600">Try adjusting your filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default ExportHistory;